<template>
  <div v-show="showPagination"
       class="search-pagination">
    <button
      :disabled="pageIndex <= 0"
      class="btn btn-default"
      aria-label="Previous page"
      type="button"
      @click="previousPage()">
      <font-awesome-icon icon="chevron-left"
                         aria-hidden="true"
                         class="icon"></font-awesome-icon>
      <span class="hide-small">
        Previous
      </span>
    </button>

    <div class="search-pagination__page-number-wrapper">
      <span
        v-for="(page, index) in availablePages"
        :key="index"
        :class="pageNumberClass(page)"
        @click="selectPage(page)">
        {{ page }}
      </span>
    </div>

    <button
      :disabled="pageIndex >= pageCount - 1"
      class="btn btn-default"
      aria-label="Next page"
      type="button"
      @click="nextPage()">
      <span class="hide-small">
        Next
      </span>
      <font-awesome-icon icon="chevron-right"
                         aria-hidden="true"
                         class="icon"></font-awesome-icon>
    </button>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  components: {},
  props: {
    enableScrolling: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      selectedPage: 0,
      pagePadding: 2,
    };
  },
  computed: {
    maxPagePadding () {
      return this.pagePadding * 2 + 1;
    },
    min () {
      const pagesLeft = this.pageCount - this.pageNumber;
      const totalPadding = this.maxPagePadding - this.pagePadding;
      const endPadding = Math.max( 0, totalPadding - pagesLeft - 1 );

      return Math.max( 1, this.pageNumber - this.pagePadding - endPadding );
    },
    max () {
      const startPadding = Math.abs(
        Math.min( 0, this.pageNumber - this.maxPagePadding + this.pagePadding ),
      );
      return Math.min( this.pageCount, this.pageNumber + this.pagePadding + startPadding );
    },
    pageNumber () {
      return this.pageIndex + 1;
    },
    pageCount () {
      return Math.ceil( this.resultCount / this.itemsPerPage );
    },
    showPagination () {
      return this.pageCount > 1;
    },
    availablePages () {
      const result = this.fillArray( this.min, this.max );

      // Only worry about adding elipses if there are enough pages
      if ( this.pageCount + 1 >= this.pagePadding + this.maxPagePadding ) {
        // If we are not near the end, we might want elipses on the right
        const rightPaddedPage = this.pageNumber + this.pagePadding;
        if ( rightPaddedPage <= this.pageCount - 1 ) {
          const distance = Math.abs( result[result.length - 1] - this.pageCount );
          const willOmitPages = rightPaddedPage + 1 <= this.pageCount - 1;

          if ( willOmitPages && distance === 2 ) {
            result.push( String( this.pageCount - 1 ) );
          } else if ( willOmitPages && distance > 2 ) {
            result.push( "..." );
          }

          result.push( this.pageCount );
        }

        // If we are not near the beginning, we might want elipses on the left
        const leftPaddedPage = this.pageNumber - this.pagePadding;
        if ( leftPaddedPage > 1 ) {
          const distance = Math.abs( result[0] - 1 );
          const willOmitPages = leftPaddedPage + 1 > 1;

          result.splice( 0, 0, "1" );

          if ( willOmitPages && distance === 2 ) {
            result.splice( 1, 0, "2" );
          } else if ( willOmitPages && distance > 2 ) {
            result.splice( 1, 0, "..." );
          }
        }
      }

      return result;
    },
    ...mapState( {
      pageIndex: state => state.search.pageIndex,
      itemsPerPage: state => state.search.itemsPerPage,
      resultCount: state => state.search.resultCount,
    } ),
  },
  watch: {
    pageNumber ( newValue ) {
      this.selectedPage = newValue;
    },
  },
  created () {
    this.selectedPage = this.pageNumber;
  },
  methods: {
    fillArray ( min, max ) {
      const length = max - min + 1;
      return Array.from( new Array( length ), ( _, i ) => String( min + i ) );
    },
    pageNumberClass ( number ) {
      let result = "";

      if ( number === "..." ) {
        result = "search-pagination__elipsis";
      } else if ( Number.parseInt( number, 10 ) === this.pageNumber ) {
        result = "search-pagination__page-number search-pagination__page-number--selected";
      } else {
        result = "search-pagination__page-number";
      }

      return result;
    },
    nextPage () {
      this.changePage( this.pageIndex + 1 );
    },
    previousPage () {
      this.changePage( this.pageIndex - 1 );
    },
    changePage ( pageIndex ) {
      const payload = {
        pageIndex,
        scrollToTop: this.enableScrolling,
      };

      this.$store.dispatch( "paginate", payload );
    },
    isElipsis ( value ) {
      return value === "...";
    },
    selectPage ( value ) {
      if ( value !== "..." ) {
        this.selectedPage = value;
        this.changePage( value - 1 );
      }
    },
  },
};
</script>

<style lang="less" scoped>
.search-pagination {
  margin-bottom: 10px;
  padding: 10px;
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;

  &__page-number-wrapper {
    margin: 0 15px;
    padding: 5px;
    display: flex;
  }

  &__page-number {
    font-weight: 600;
    font-size: 1.2rem;
    line-height: 1.75;
    text-align: center;
    margin: 0 5px;
    width: 31px;
    color: #666666;

    @media (max-width: 767px) {
      font-size: 1rem;
      margin: 0;
    }

    &:hover {
      cursor: pointer;

      &:after {
        content: " ";
        display: block;
        width: 100%;
        height: 3px;
        background-color: #d71920;
      }
    }

    &--selected {
      color: #d71920;

      &:after {
        content: " ";
        display: block;
        width: 100%;
        height: 3px;
        background-color: #d71920;
      }
    }
  }

  &__elipsis {
    font-size: 1.2rem;
    line-height: 1.75;
    text-align: center;
    margin: 0 5px;
    width: 31px;
    color: #666666;

    @media (max-width: 767px) {
      font-size: 1rem;
      margin: 0;
    }
  }

  button {
    padding: 5px 10px;
    font-size: 1.2rem;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (min-width: 768px) {
      min-width: 120px;
    }

    @media (max-width: 767px) {
      font-size: 1rem;
      padding: 0;
    }

    .icon {
      margin: 0 10px;
      color: #d71920;

      @media (max-width: 767px) {
        margin: 0 5px;
      }
    }
  }

  .select-wrapper {
    width: 80px;
    display: inline-block;
    margin: 0 10px;
  }

  .hide-small {
    @media (max-width: 767px) {
      display: none;
    }
  }
}
</style>
